import React, { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { NavLink, Link, useLocation } from "react-router-dom";
import Logo from "@/components/Logo";

const NAV = [
    { label: "Home", to: "/" },
    { label: "About", to: "/about" },
    { label: "Services", to: "/services" },
    { label: "Vantage Benefits", to: "/vantage-markets" },
    { label: "Insights", to: "/insights" },
    { label: "Contact", to: "/contact" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled || open || location.pathname !== "/" ? "glass" : "bg-transparent"
            }`}
            data-testid="site-header"
        >
            <div className="container-x flex items-center justify-between py-4">
                <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
                    <Logo height={84} />
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === "/"}
                            className={({ isActive }) =>
                                `text-[13px] tracking-wide transition-colors ${
                                    isActive
                                        ? "text-[#C8A064]"
                                        : "text-[#94A3B8] hover:text-[#C8A064]"
                                }`
                            }
                            data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <Link to="/contact" className="btn-brass" data-testid="book-a-call-cta">
                        <Phone size={14} /> Book a Call
                    </Link>
                </div>

                <button
                    className="lg:hidden text-[#F3F4F6]"
                    onClick={() => setOpen((s) => !s)}
                    aria-label="Toggle menu"
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden glass border-t border-white/5" data-testid="mobile-menu">
                    <div className="container-x py-6 flex flex-col gap-4">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                className={({ isActive }) =>
                                    `text-left py-2 transition-colors ${
                                        isActive
                                            ? "text-[#C8A064]"
                                            : "text-[#F3F4F6] hover:text-[#C8A064]"
                                    }`
                                }
                                data-testid={`mobile-nav-${n.label
                                    .toLowerCase()
                                    .replace(/\s/g, "-")}`}
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            className="btn-brass justify-center w-full"
                            data-testid="mobile-book-a-call-cta"
                        >
                            <Phone size={14} /> Book a Call
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
